/**
 * * External Imports
 */
import { FC, useMemo, useState, Dispatch, SetStateAction } from 'react';
import { connect } from 'react-redux';

/**
 * Nexus Imports
 */
import { NeuIcon, NeuCheckbox } from '@neutron/react';

/**
 * Internal Imports
 */
import Table from '../../../shared/table';

import { RootState } from '../../../../redux/store';
import {
  useLazyGetTemplateByIdQuery,
  usePostTemplateConfigMutation
} from '../../../../redux/apis/Templates.api';

import { date } from '../../../../utils/helpers';

/**
 * Style Imports
 */
import {
  CellContainer,
  StyledLabel,
  StyledNeuTableRow
} from '../../../shared/table/Table.styles';

/**
 * Global Type Definition Imports
 */
import { AuthUserProps, TreeNode } from '../../../../config/interfaces';

interface TemplateTreeTableProps {
  authUser: AuthUserProps;
  level: string;
  roundingType: string;
  treeData: TreeNode[];
  handleTableRowClick: (data: any, level: string) => void;
  setSelectedTemplateDocId: Dispatch<SetStateAction<string>>;
  setShowEditTemplateFlag: Dispatch<SetStateAction<boolean>>;
}

/**
 * Table component for Admin -> Templates page filter by type of template
 * @param props {Object} - Division list by template and if it is published/not published, when the template was last update, and by who it was updated
 * @returns React Functional Component (Template Tree Table)
 */
const TemplateTreeTable: FC<TemplateTreeTableProps> = ({
  authUser,
  level,
  roundingType,
  treeData = [],
  handleTableRowClick,
  setSelectedTemplateDocId,
  setShowEditTemplateFlag
}) => {
  /**
   * Local state for array of dynamic status properties
   */
  const [sortKey, setSortKey] = useState<string>('name');
  const [sortDir, setSortDir] = useState<any>('asc');
  const [active, setActive] = useState<string>(level);

  const [
    getTemplateById
    // {
    //   data: templateByIdData = {},
    //   isError: isGetTemplateByIdError,
    //   isFetching: isGetTemplateByIdFetching,
    //   isLoading: isGetTemplateByIdLoading
    // }
  ] = useLazyGetTemplateByIdQuery();
  const [
    postTemplateConfig
    // {
    //   isError: isPostTemplateConfigError,
    //   isLoading: isPostTemplateConfigLoading,
    //   isSuccess: isPostTemplateConfigSuccess
    // }
  ] = usePostTemplateConfigMutation();

  /**
   * Edit template
   * @param item - current template
   */
  const handleEdit = (item: any) => {
    // appInsights.trackEvent({
    //   name: 'Template_Changed'
    // });
    setSelectedTemplateDocId(item.data.templateKey);
    setShowEditTemplateFlag(true);
  };

  /**
   * If published check box is selected/deselected update changes to the API
   * @param item - current line item - division name, published(?), updated when/who
   */
  const handlePublishedChange = async (item: any) => {
    const isPublished = !item.data.isPublished;

    try {
      const { data: templateByIdData } = await getTemplateById(
        item.data.templateConfigId
      );

      const now = date.now();
      const nowUTC = new Date().toISOString();
      const payload = {
        ...templateByIdData,
        userId: authUser.hcaid,
        isPublished,
        templateLevel: item.data.level,
        templateLevelId: item.data.levelId,
        lastUpdated: nowUTC,
        lastUpdatedBy: authUser.hcaid,
        lastUpdatedByName: `${authUser.firstName} ${authUser.lastName}`,
        lastUpdatedDisplay: `${date.datePrettier(now)} ${date.localTimeOfDay(
          now
        )}`,
        lastUpdatedUTC: nowUTC
      };
      delete payload.templateId;
      await postTemplateConfig(payload);
    } catch (error) {
      console.error('Error updating template config:', error);
    }
  };

  const handleSort = (column: string) => {
    let key;
    switch (column) {
      case 'Division':
      case 'Facility':
      case 'Unit':
        key = 'name';
        break;
      case 'Published':
        key = 'isPublished';
        break;
      case 'Last Updated':
        key = 'lastUpdated';
        break;
      case 'Updated By':
        key = 'lastUpdatedByName';
        break;
      case 'Configured':
        key = 'hasConfig';
        break;
      default:
        key = '';
    }
    if (key === sortKey && sortDir === 'asc') {
      setSortDir('desc');
    } else {
      setSortDir('asc');
    }
    setActive(column);
    setSortKey(key);
  };

  /**
   * Sort by key if selected
   * @param key - table header
   */
  const sortedTree = useMemo(() => {
    const sortCopy = [...treeData];
    sortCopy.sort((a: any, b: any) =>
      a.data[sortKey] > b.data[sortKey]
        ? sortDir === 'asc'
          ? 1
          : -1
        : sortDir === 'asc'
        ? -1
        : 1
    );
    return sortCopy;
  }, [treeData, sortKey, sortDir]);

  const isTableEmpty = useMemo(() => sortedTree.length === 0, [sortedTree]);

  const headers: JSX.Element[] = [
    <CellContainer
      key="level"
      id="Template-Tree-Level-Column"
      unit="column"
      colWidth={roundingType === 'patient' ? 25 : 30}
      direction={!(active === level) ? 'none' : sortDir}
      sort
    >
      {level}
    </CellContainer>,
    ...(roundingType === 'patient'
      ? [
          <CellContainer
            key="Published"
            id="Template-Tree-Published-Column"
            unit="column"
            colWidth={25}
            direction={!(active === 'Published') ? 'none' : sortDir}
            sort
          >
            <div className="d-flex flex-column">
              Published
              {/* //// MAY BE UNCOMMENTED ON PHASE 2 DEVELOPMENT ///////
            <div
              style={{
                display: 'flex',
                flexDirection: 'row',
                fontSize: 12,
                justifyContent: 'space-between'
              }}
            >
              <NeuCheckbox style={{ marginRight: 5 }} />
              <NeuLabel style={{}}>All {level}</NeuLabel>
            </div> */}
            </div>
          </CellContainer>
        ]
      : []),
    <CellContainer
      key="Last Updated"
      id="Template-Tree-Last-Updated-Column"
      unit="column"
      colWidth={roundingType === 'patient' ? 15 : 20}
      direction={
        !(active === 'Last Updated')
          ? 'none'
          : sortDir === 'asc'
          ? 'desc'
          : 'asc'
      }
      sort
    >
      Last Updated
    </CellContainer>,
    <CellContainer
      key="Updated By"
      id="Template-Tree-Updated-By-Column"
      unit="column"
      colWidth={roundingType === 'patient' ? 15 : 20}
      direction={!(active === 'Updated By') ? 'none' : sortDir}
      sort
    >
      Updated By
    </CellContainer>,
    <CellContainer
      key="Configured"
      id="Template-Tree-Configured-Column"
      unit="column"
      colWidth={roundingType === 'patient' ? 10 : 15}
      direction={!(active === 'Configured') ? 'none' : sortDir}
      sort
    >
      Configured
    </CellContainer>,
    <CellContainer
      key="Edit"
      id="Template-Tree-Edit-Column"
      unit="column"
      colWidth={roundingType === 'patient' ? 10 : 15}
      direction="none"
    />
  ];

  /**
   * Iterate through the listToDisplay content array and return line item for each
   */
  const rows = sortedTree?.map((item: any, i: number) => {
    return (
      <StyledNeuTableRow
        // eslint-disable-next-line react/no-array-index-key
        key={`Template-Tree-Row-${i}`}
        id={`Template-Tree-Row-${item.data.levelId}`}
        unit="row"
        size="default"
        height="5em"
        onClick={() => {
          handleTableRowClick(item, level);
        }}
      >
        <CellContainer
          unit="column"
          colWidth={roundingType === 'patient' ? 25 : 30}
        >
          <StyledLabel
            role="button"
            style={{
              whiteSpace: 'normal',
              fontWeight: 700,
              color: ' #206DA3'
            }}
          >
            {item.data.name}
          </StyledLabel>
        </CellContainer>

        {roundingType === 'patient' && (
          <CellContainer unit="column" colWidth={25}>
            <StyledLabel className="d-flex align-items-center" role="button">
              <NeuCheckbox
                checked={item.data.isPublished === true}
                color="blue"
                label="Published"
                onClick={e => {
                  e.stopPropagation();
                  handlePublishedChange(item);
                }}
              />
            </StyledLabel>
          </CellContainer>
        )}
        <CellContainer
          unit="column"
          colWidth={roundingType === 'patient' ? 15 : 20}
        >
          <StyledLabel style={{ whiteSpace: 'normal' }}>
            {item.data.lastUpdated
              ? new Date(item.data.lastUpdated)
                  .toLocaleString([], {
                    day: '2-digit',
                    month: '2-digit',
                    year: '2-digit',
                    hour: '2-digit',
                    minute: '2-digit'
                  })
                  .replace(',', '')
              : ''}
          </StyledLabel>
        </CellContainer>
        <CellContainer
          unit="column"
          colWidth={roundingType === 'patient' ? 15 : 20}
        >
          <StyledLabel style={{ whiteSpace: 'normal' }}>
            <div>
              {item.data.lastUpdated && item.data.lastUpdatedByName
                ? item.data.lastUpdatedByName
                : item.data.lastUpdated && !item.data.lastUpdatedByName
                ? 'Unknown'
                : ''}
            </div>
            <div>
              {item.data.lastUpdated && item.data.lastUpdatedBy
                ? item.data.lastUpdatedBy
                : item.data.lastUpdated && !item.data.lastUpdatedBy
                ? 'Unknown'
                : ''}
            </div>
          </StyledLabel>
        </CellContainer>
        <CellContainer
          unit="column"
          colWidth={roundingType === 'patient' ? 10 : 15}
        >
          <StyledLabel>
            <div>{item.data.hasConfig ? 'Yes' : 'No'}</div>
          </StyledLabel>
        </CellContainer>
        <CellContainer
          unit="column"
          colWidth={roundingType === 'patient' ? 10 : 15}
        >
          <StyledLabel
            className="d-flex justify-content-end pe-4"
            role="button"
            style={{ fontWeight: 700, color: '#206DA3' }}
            onClick={
              level.toLowerCase() === authUser.access.accessLevel ||
              authUser.access.accessLevel === 'division' ||
              authUser.access.accessLevel === 'master' ||
              level === 'Unit'
                ? () => handleEdit(item)
                : undefined
            }
          >
            {(level.toLowerCase() === authUser.access.accessLevel ||
              authUser.access.accessLevel === 'division' ||
              authUser.access.accessLevel === 'master' ||
              level === 'Unit') && (
              <>
                <NeuIcon class="me-1" fill color="blue">
                  edit
                </NeuIcon>
                Edit
              </>
            )}
          </StyledLabel>
        </CellContainer>
      </StyledNeuTableRow>
    );
  });

  return (
    <Table
      customClass="admin-areas-unit-table"
      headers={headers}
      rows={rows}
      isTableEmpty={isTableEmpty}
      type="templates"
      onSort={(e: any) => handleSort(e.detail.label)}
    />
  );
};

const mapReduxStateToProps = (state: RootState) => {
  return {
    authUser: state.AuthorizedUser.authorizedUser
  };
};

export default connect(mapReduxStateToProps)(TemplateTreeTable);
